import { useState } from 'react';
import { MessageSquare, Edit2, Trash2, Plus, Check, X } from 'lucide-react';
import { AISession, aiAssistant } from '../lib/aiCareerAssistant';

interface SessionManagerProps {
  sessions: AISession[];
  currentSessionId: string | null;
  onSelectSession: (sessionId: string) => void;
  onCreateSession: () => void;
  onRefreshSessions: () => void;
}

export function SessionManager({
  sessions,
  currentSessionId,
  onSelectSession,
  onCreateSession,
  onRefreshSessions,
}: SessionManagerProps) {
  const [editingSessionId, setEditingSessionId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');
  const [deletingSessionId, setDeletingSessionId] = useState<string | null>(null);

  const handleStartEdit = (session: AISession) => {
    setEditingSessionId(session.id);
    setEditingName(session.session_name || 'Unnamed Session');
  };

  const handleSaveEdit = async () => {
    if (!editingSessionId) return;

    const success = await aiAssistant.updateSessionName(editingSessionId, editingName);
    if (success) {
      setEditingSessionId(null);
      onRefreshSessions();
    }
  };

  const handleCancelEdit = () => {
    setEditingSessionId(null);
    setEditingName('');
  };

  const handleDelete = async (sessionId: string) => {
    if (deletingSessionId === sessionId) {
      const success = await aiAssistant.deleteSession(sessionId);
      if (success) {
        setDeletingSessionId(null);
        if (sessionId === currentSessionId) {
          onCreateSession();
        } else {
          onRefreshSessions();
        }
      }
    } else {
      setDeletingSessionId(sessionId);
      setTimeout(() => setDeletingSessionId(null), 3000);
    }
  };

  return (
    <div className="bg-white rounded-2xl p-6 shadow-lg border-2 border-gray-200">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
          <MessageSquare className="w-5 h-5 text-cyan-500" />
          Chat History
        </h2>
        <button
          onClick={onCreateSession}
          className="flex items-center gap-2 px-3 py-1.5 bg-cyan-500 text-white rounded-lg text-sm font-semibold hover:bg-cyan-600 transition-colors"
        >
          <Plus className="w-4 h-4" />
          New
        </button>
      </div>

      <div className="space-y-2 max-h-96 overflow-y-auto">
        {sessions.length === 0 && (
          <p className="text-gray-500 text-sm text-center py-4">No chat sessions yet</p>
        )}

        {sessions.map((session) => {
          const isActive = session.id === currentSessionId;
          const isEditing = session.id === editingSessionId;
          const isDeleting = session.id === deletingSessionId;

          return (
            <div
              key={session.id}
              className={`p-3 rounded-xl border-2 transition-all ${
                isActive
                  ? 'bg-cyan-50 border-cyan-500'
                  : 'bg-gray-50 border-gray-200 hover:border-gray-300'
              }`}
            >
              {isEditing ? (
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={editingName}
                    onChange={(e) => setEditingName(e.target.value)}
                    className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm focus:border-cyan-500 focus:outline-none"
                    autoFocus
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') handleSaveEdit();
                      if (e.key === 'Escape') handleCancelEdit();
                    }}
                  />
                  <button
                    onClick={handleSaveEdit}
                    className="p-1 hover:bg-green-100 rounded transition-colors"
                  >
                    <Check className="w-4 h-4 text-green-600" />
                  </button>
                  <button
                    onClick={handleCancelEdit}
                    className="p-1 hover:bg-red-100 rounded transition-colors"
                  >
                    <X className="w-4 h-4 text-red-600" />
                  </button>
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  <button
                    onClick={() => onSelectSession(session.id)}
                    className="flex-1 text-left"
                  >
                    <p className={`font-semibold text-sm ${isActive ? 'text-cyan-900' : 'text-gray-900'}`}>
                      {session.session_name || 'Unnamed Session'}
                    </p>
                    <p className="text-xs text-gray-500">
                      {new Date(session.last_activity).toLocaleDateString()}
                    </p>
                  </button>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => handleStartEdit(session)}
                      className="p-1 hover:bg-gray-200 rounded transition-colors"
                    >
                      <Edit2 className="w-3.5 h-3.5 text-gray-600" />
                    </button>
                    <button
                      onClick={() => handleDelete(session.id)}
                      className={`p-1 rounded transition-colors ${
                        isDeleting ? 'bg-red-100' : 'hover:bg-gray-200'
                      }`}
                    >
                      <Trash2 className={`w-3.5 h-3.5 ${isDeleting ? 'text-red-600' : 'text-gray-600'}`} />
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
